import { Component } from '@theme/component';

/**
 * Summit Index window (home, sections/zerbay-summit-index.liquid).
 *
 * From 1180px a window beside the list shows a vertical strip of large photos, one cell per row in row order.
 * Pointer enter or focus on a row marks it active and slides the strip to its cell. The first row is active by
 * default. The window images stay lazy until the first desktop activation, then switch to eager. Below 1180px
 * (and without a strip) nothing happens; the window itself is hidden by CSS.
 *
 * Refs: `rows[]` (one link per row, in order), `strip` (optional; absent when no row has a photo).
 *
 * @extends {Component<{ rows?: HTMLElement[], strip?: HTMLElement }>}
 */
class ZerbaySummitIndex extends Component {
  #desktop = window.matchMedia('(min-width: 1180px)');
  #eager = false;
  #active = 0;

  connectedCallback() {
    super.connectedCallback();
    // pointerenter does not bubble: listen in the capture phase on the host. focusin bubbles.
    this.addEventListener('pointerenter', this.#onEnter, true);
    this.addEventListener('focusin', this.#onEnter);
    this.#desktop.addEventListener('change', this.#onViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#onEnter, true);
    this.removeEventListener('focusin', this.#onEnter);
    this.#desktop.removeEventListener('change', this.#onViewportChange);
  }

  /** @param {Event} event */
  #onEnter = (event) => {
    const { rows } = this.refs;
    if (!rows || !(event.target instanceof Element)) return;

    const row = event.target.closest('[ref="rows[]"]');
    const index = row ? rows.indexOf(/** @type {HTMLElement} */ (row)) : -1;
    if (index === -1) return;

    this.#show(index);
  };

  #onViewportChange = () => {
    if (this.#desktop.matches) this.#show(this.#active);
  };

  /** @param {number} index */
  #show(index) {
    const { rows, strip } = this.refs;
    if (!this.#desktop.matches || !rows || !strip) return;

    if (!this.#eager) {
      this.#eager = true;
      for (const image of strip.querySelectorAll('img')) image.loading = 'eager';
    }

    this.#active = index;
    strip.style.transform = `translateY(${(-100 / rows.length) * index}%)`;
    rows.forEach((row, i) => row.classList.toggle('zb-sum__row--on', i === index));
  }
}

if (!customElements.get('zerbay-summit-index')) {
  customElements.define('zerbay-summit-index', ZerbaySummitIndex);
}
